import surveyXFields from '@/views/Products/store/survey-xfields.json'

/**
 * Mapa de perguntas da pesquisa de satisfação derivado do próprio formulário
 * (survey-xfields.json), de forma que relatório e formulário nunca divirjam.
 *
 * Cada gestor habilita um subconjunto das perguntas, então o relatório exibe
 * apenas os slugs que voltarem com respostas na rota /survey/nps-report.
 */

const RATING_TYPES = ['radio', 'select', 'slider']
const OPEN_TEXT_TYPES = ['textarea']

/**
 * A escala vem das próprias opções do xfield (`value=N`), porque as perguntas
 * não usam todas a mesma régua: satisfação vai de 1 a 5 e disponibilidade de
 * informação vai de 1 a 3 (Insuficiente / Adequada / Excelente).
 */
function parseScale(field) {
  const options = (field && field.options) || {}
  let max = 0
  let valuedOptions = 0

  Object.keys(options).forEach((option) => {
    const match = /value=(\d+)/.exec(String(option))
    if (!match) return
    max = Math.max(max, Number(match[1]))
    valuedOptions += 1
  })

  if (max > 0) return max

  return valuedOptions
}

function buildSurveyQuestions() {
  const questions = {}
  let session = ''
  let order = 0

  Object.keys(surveyXFields).forEach((key) => {
    const field = surveyXFields[key] || {}
    const slug = field.label || key
    const title = (field.public_label || slug).split('#')[0]

    if (field.type === 'hidden_config') {
      if (String(slug).indexOf('session_title') === 0) {
        session = title
      }
      return
    }

    order += 1
    const scale = parseScale(field)

    questions[slug] = {
      slug,
      title,
      session,
      order,
      scale,
      type: field.type || 'text',
      isRating: RATING_TYPES.indexOf(field.type) >= 0 && scale > 1,
      isOpenText: OPEN_TEXT_TYPES.indexOf(field.type) >= 0,
    }
  })

  return questions
}

export const surveyQuestions = buildSurveyQuestions()

/**
 * Slugs que ainda chegam de coletas antigas do Zoho e não existem mais no
 * formulário. Slugs desconhecidos fora desta lista entram no relatório com o
 * título humanizado a partir do próprio slug.
 */
// const LEGACY_QUESTIONS = {
//   csat: { title: 'Qualidade geral da visitação', scale: 5, type: 'radio' },
// }

/**
 * Substitui o antigo mapa manual zohoSlugTitles do relatório: todo slug do
 * formulário está presente e ativo, sem risco de divergir do .json.
 */
export const surveySlugTitles = Object.keys(surveyQuestions).reduce(
  (acc, slug) => {
    const question = surveyQuestions[slug]
    acc[slug] = {
      title: question.title,
      session: question.session,
      group: slug,
      order: question.order,
      scale: question.scale,
      type: question.type,
      active: true,
    }
    return acc
  },
  {}
)

// Object.keys(LEGACY_QUESTIONS).forEach((slug) => {
//   if (surveySlugTitles[slug] != undefined) return
//   surveySlugTitles[slug] = {
//     ...LEGACY_QUESTIONS[slug],
//     session: '',
//     group: slug,
//     order: 900,
//     active: true,
//   }
// })

export const OFFICIAL_SCORE_SLUG = 'nps'
export const OPEN_TEXT_SLUG = 'suggestions'

export function humanizeSlugTitle(slug) {
  return String(slug || '')
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
}

export function resolveSurveySlug(slug, slugTitles) {
  const titles = slugTitles || surveySlugTitles
  const raw = String(slug || '')
  if (titles[raw] != undefined) return raw

  const lower = raw.toLowerCase()
  if (titles[lower] != undefined) return lower

  const found = Object.keys(titles).find((key) => key.toLowerCase() === lower)

  return found || raw
}

/**
 * Faixas de nota consideradas negativas, neutras e positivas em cada escala.
 * Em escalas de 5 pontos o CSAT são as notas 4 e 5 e a cauda negativa as notas
 * 1 e 2. Em escalas de 3 pontos só a nota máxima conta como satisfação.
 */
export function ratingBuckets(scale) {
  const max = Number(scale) || 5
  const values = []
  for (let value = 1; value <= max; value++) values.push(value)

  const edge = max >= 5 ? 2 : 1

  return {
    negative: values.slice(0, edge),
    positive: values.slice(-edge),
    neutral: values.slice(edge, max - edge),
  }
}
